'use client';

import { cn } from '@/lib/utils';
import { motion, useTransform, MotionValue } from 'motion/react';

const CARD_WIDTH = 600;
const CARD_GAP = 24;

interface FocusCardDesktopProps {
  icon: React.ComponentType<{ width?: number; height?: number }>;
  title: string;
  text: string;
  index: number;
  scrollYProgress: MotionValue<number>;
  cardsCount: number;
}

export function FocusCardDesktop({ 
  icon: IconComponent, 
  title, 
  text, 
  index,
  scrollYProgress,
  cardsCount
}: FocusCardDesktopProps) {
  // Calculate individual card position
  const progressPerCard = 1 / (cardsCount - 1);
  const cardStartProgress = index === 0 ? 0 : (index - 1) * progressPerCard;
  const cardEndProgress = Math.min(1, cardStartProgress + progressPerCard);
  
  // Initial position (spread out)
  const initialX = index * (CARD_WIDTH + CARD_GAP);
  // Final position (stacked with offset to show icon of previous card)
  const finalX = index * 140;
  
  // Use Framer Motion's useTransform for smooth animations
  const x = useTransform(
    scrollYProgress,
    index === 0 
      ? [0, 1] 
      : [cardStartProgress, cardEndProgress],
    index === 0 
      ? [0, 0] 
      : [initialX, finalX]
  );

  return (
    <motion.div
      className='absolute top-0 left-0 w-[600px]'
      style={{ 
        x,
        zIndex: index + 1,
      }}
    >
      <div className={cn('card-gradient-border rounded-3xl shadow-2xl w-[600px] h-[350px]')}>
        <div className={cn('h-full rounded-3xl p-12')} style={{ background: '#1a1a2e' }}>
          <div className={cn('flex flex-row gap-6')}>
            <div className='shrink-0'>
              <IconComponent width={89} height={102} />
            </div>
            <div className='flex flex-col flex-1'>
              <h3 className='text-white text-2xl font-semibold mb-4 leading-tight'>{title}</h3>
              <p className='text-brand-violet-100 text-sm font-normal leading-relaxed whitespace-pre-line'>
                {text}
              </p>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
